import { FormStore, GlobalStore } from '@roc/feature-app-core';
import {
  debounceRequest,
  generateUUID,
  GENERIC_ERROR_MESSAGE,
  isNumber,
} from '@roc/feature-utils';
import { Option } from '@roc/ui/utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { QuoteService } from '../../services/quoteService';
import { LoanSubType } from '../../utils/constants';
import { isBridgeLoan } from '../../utils/utils';

const REQUIRED_MESSAGE = 'This field is required';

const borrowerForm = {
  fields: {
    borrowerId: {
      value: '',
    },
    firstName: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    lastName: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    emailAddress: {
      value: '',
      error: null,
      rule: 'email',
      message: 'Email is invalid.',
    },
    ficoScore: {
      value: '',
      error: null,
      rule: 'numeric|min:300|max:850',
      message: 'FICO Score must be a number between 300 and 850.',
    },
    experience: {
      value: '',
      error: null,
      rule: '',
    },
    personalGuarantor: {
      value: true,
      error: null,
      rule: '',
    },
    percentageOfOwnership: {
      value: 0,
      error: null,
      rule: '',
    },
    citizenshipStatus: {
      value: '',
      error: null,
      rule: '',
    },

    bankruptcy: {
      value: 'N',
      error: null,
      rule: '',
    },
    bankruptcyDate: {
      value: null,
      error: null,
      rule: '',
    },
    foreclosure: {
      value: 'N',
      error: null,
      rule: '',
    },
    foreclosureDate: {
      value: null,
      error: null,
      rule: '',
    },
  },
  meta: {
    editMode: false,
    isValid: false,
    showErrors: null,
    error: null,
  },
};

export class BorrowerFormStore extends FormStore {
  private globalStore: GlobalStore;
  private quoteService: QuoteService;
  private loanSubtype: LoanSubType;

  public key = generateUUID();

  borrowerOptions: Option[] = [];

  existingBorrowerData: any;
  savedBorrower: any;

  findBorrowersByNameDebounced = debounceRequest((cancelToken, name) =>
    this.findBorrowersByName(name, cancelToken)
  );

  constructor(globalStore, loanSubtype?: LoanSubType) {
    super(borrowerForm, globalStore);
    this.globalStore = globalStore;
    this.quoteService = new QuoteService();
    this.loanSubtype = loanSubtype;

    this.prepopulateNewBorrower();
    makeObservable(this, {
      borrowerOptions: observable,
      findBorrowersByName: flow,
      fetchBorrower: flow,
      existingBorrowerData: observable,
      loadExistingBorrower: action,
      isNewBorrower: computed,
    });
  }

  reset() {
    super.reset();
    this.existingBorrowerData = null;
    this.savedBorrower = null;
    this.setEditMode(true);
  }

  prepopulateNewBorrower() {
    this.form.meta.editMode = true;
  }

  setShowErrors(value) {
    this.form.meta.showErrors = value;
  }
  setEditMode(value) {
    this.form.meta.editMode = value;

    if (value) {
      this.setShowErrors(null);
    }
  }

  *findBorrowersByName(name, cancelToken?) {
    try {
      if (name.length >= 3) {
        const response = yield this.quoteService.findBorrowersByName(
          name,
          cancelToken
        );
        this.borrowerOptions = response.data.data;
      } else {
        this.borrowerOptions = [];
      }
    } catch (e) {
      console.log(e);
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  overrideGroundUpExperience(groundUpExperience) {
    const GROUND_UP_EXPERIENCE = 0;
    const GROUND_UP_EXPERIENCE_1 = 1;
    const GROUND_UP_EXPERIENCE_2 = 2;
    if (
      groundUpExperience === GROUND_UP_EXPERIENCE ||
      groundUpExperience === GROUND_UP_EXPERIENCE_1 ||
      groundUpExperience === GROUND_UP_EXPERIENCE_2
    ) {
      return GROUND_UP_EXPERIENCE_1;
    } else {
      return groundUpExperience;
    }
  }

  *fetchBorrower(recordId) {
    try {
      const recordComposedKey = recordId.split('-');
      const id = recordComposedKey[1];
      const response = yield this.quoteService.fetchBorrowerData(id);
      const recordData = response.data;
      this.loadExistingBorrower(recordData);
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  loadExistingBorrower(borrower) {
    this.existingBorrowerData = borrower;

    const ffExperience = borrower.experience === 0 ? '' : borrower.experience;
    const guExperience = borrower.groundupExperience
      ? this.overrideGroundUpExperience(borrower.groundupExperience)
      : '';
    const rentalExperience =
      borrower.rentalExperience === 0 ? '' : borrower.experience;

    const bridgeExperience =
      LoanSubType.GROUND_UP === this.loanSubtype ? guExperience : ffExperience;

    const ficoScore =
      borrower.ficoScore ||
      borrower.medianScore ||
      borrower.ficoProvidedAtOrigination ||
      '';
    const phone =
      borrower.cellPhone ||
      borrower.phone ||
      borrower.homePhone ||
      borrower.secondaryPhone ||
      '';
    const percentageOfOwnership =
      borrower.percentOwnershipCurrent || borrower.percentageOfOwnership || 0;

    this.loadBorrower({
      borrowerId: borrower.borrowerId,
      personal: {
        firstName: borrower.firstName,
        lastName: borrower.lastName,
        email: borrower.emailAddress,
        phone: phone,
        citizenshipStatus: borrower.citizenshipStatus,
      },
      experience: {
        experience: borrower.experience,
        fundedRetailLoans: borrower.fundedRetailLoans,
        rentalExperience: rentalExperience,
        groundUpExperience: null,
      },
      credit: {
        ficoScore: ficoScore,
        medianScore: null,
      },
      financials: {
        assets: borrower.assets,
        liabilities: borrower.liabilities,
      },
      roles: {
        personalGuarantor: borrower.personalGuarantor,
      },
      ownership: {
        percentageOfOwnership: percentageOfOwnership,
      },
      flags: {
        blacklisted: borrower.blacklisted,
      },
    });
  }

  getIdFromComposedKey(composedKey) {
    if (!isNumber(composedKey) && composedKey.includes('-')) {
      const recordComposedKey = composedKey.split('-');
      return recordComposedKey[1];
    }
    return composedKey;
  }

  getBorrower() {
    const values = this.getFormValues();
    const isBridge = isBridgeLoan(this.loanSubtype);
    const savedBorrower = this.savedBorrower ?? {};
    return {
      ...savedBorrower,
      borrowerId: values.borrowerId,
      uuid: values.uuid,
      personal: {
        ...(savedBorrower.personal ?? {}),
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.emailAddress,
        phone: values.phone,
        citizenshipStatus: values.citizenshipStatus,
      },
      experience: {
        ...(savedBorrower.experience ?? {}),
        experience: isBridge ? values.experience : null,
        fundedRetailLoans: null,
        rentalExperience: isBridge ? null : values.experience,
        groundUpExperience: null,
      },
      roles: {
        ...(savedBorrower.roles ?? {}),
        personalGuarantor: values.personalGuarantor,
      },
      credit: {
        ...(savedBorrower.credit ?? {}),
        ficoScore: values.ficoScore ? parseInt(values.ficoScore) : null,
        medianScore: null,
        bankruptcyDate: values.bankruptcy == 'Y' ? values.bankruptcyDate : null,
        foreclosureDate:
          values.foreclosure == 'Y' ? values.foreclosureDate : null,
      },
      ownership: {
        ...(savedBorrower.ownership ?? {}),
        percentageOfOwnership: values.percentageOfOwnership,
      },
    };
  }

  loadBorrower(borrower) {
    this.savedBorrower = borrower;
    this.key = borrower.uuid ?? this.key;
    const experience = isBridgeLoan(this.loanSubtype)
      ? borrower.experience?.experience
      : borrower.experience?.rentalExperience;
    if (!this.existingBorrowerData) {
      this.existingBorrowerData = {
        firstName: borrower.personal?.firstName,
        lastName: borrower.personal?.lastName,
        emailAddress: borrower.personal?.email,
        ficoScore: borrower.credit?.ficoScore,
        medianScore: borrower.credit?.medianScore,
        experience: experience,
        rentalExperience: borrower.experience?.rentalExperience,
        groundupExperience: borrower.experience?.groundUpExperience,
      };
    }
    this.loadForm({
      borrowerId: borrower.borrowerId,
      firstName: borrower.personal?.firstName ?? '',
      lastName: borrower.personal?.lastName ?? '',
      emailAddress: borrower.personal?.email ?? '',
      ficoScore: borrower.credit?.ficoScore ?? '',
      experience: experience ?? '',
      personalGuarantor: borrower.roles?.personalGuarantor ?? true,
      percentageOfOwnership: borrower.ownership?.percentageOfOwnership ?? 0,
      citizenshipStatus: borrower.personal?.citizenshipStatus ?? '',
      bankruptcy: borrower.credit?.bankruptcyDate ? 'Y' : 'N',
      bankruptcyDate: borrower.credit?.bankruptcyDate,
      foreclosure: borrower.credit?.foreclosureDate ? 'Y' : 'N',
      foreclosureDate: borrower.credit?.foreclosureDate,
    });
  }

  validateBorrower() {
    this.runFormValidationWithMessage();
    this.setShowErrors(!this.form.meta.isValid);
    return this.form.meta.isValid;
  }

  get isNewBorrower() {
    return !this.form.fields.borrowerId.value;
  }
}
